import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { catchError, filter, of, take, tap } from 'rxjs';

type VersionInfo = {
    version: string;
    hash: string;
    buildTime: string;
};

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({
    providedIn: 'root',
})
export class VersionCheckService {
    private http = inject(HttpClient);
    private router = inject(Router);

    private currentHash: string | null = null;
    private intervalId: ReturnType<typeof setInterval> | null = null;

    public readonly updateAvailable = signal(false);

    public startPolling(): void {
        this.checkVersion();
        this.intervalId = setInterval(() => this.checkVersion(), POLL_INTERVAL_MS);
    }

    public stopPolling(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    public applyUpdate(): void {
        window.location.reload();
    }

    public checkVersionAndReloadIfNeeded(): void {
        this.http
            .get<VersionInfo>(`/version.json?t=${Date.now()}`)
            .pipe(catchError(() => of(null)))
            .subscribe((versionInfo) => {
                if (!versionInfo) return;
                if (this.currentHash && this.currentHash !== versionInfo.hash) {
                    window.location.reload();
                }
                this.currentHash = versionInfo.hash;
            });
    }

    private reloadOnNextNavigation(): void {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationStart),
            take(1),
            tap((event) => {
                window.location.href = (event as NavigationStart).url;
            }),
        ).subscribe();
    }

    private checkVersion(): void {
        this.http
            .get<VersionInfo>(`/version.json?t=${Date.now()}`)
            .pipe(catchError(() => of(null)))
            .subscribe((versionInfo) => {
                if (!versionInfo) return;

                if (this.currentHash === null) {
                    this.currentHash = versionInfo.hash;
                    return;
                }

                if (this.currentHash !== versionInfo.hash) {
                    this.stopPolling();
                    this.reloadOnNextNavigation();
                }
            });
    }
}
